import { type FC, useCallback, useEffect } from 'react';
import { getPointsBuffer } from '../model';
import { getVisibleSelectedStarters } from '../selectors';
import { getEventById } from '../statistics';
import { useStore } from '../store';
import { correctedNow } from '../utils/clock';
import { useLoop } from '../utils/hooks';

interface Props {
  eventId: string;
}
const CDN_HOST = import.meta.env.VITE_CDN_HOST;

const StarterTrackManager: FC<Props> = ({ eventId }) => {
  const receiveStarterTracks = useStore((state) => state.actions.receiveStarterTracks);
  const event = useStore((state) => getEventById(state, eventId).get());
  const chunkManager = useStore((state) => state.eventsChangeable[event.index].chunkManager);
  const selected = useStore((state) =>
    getVisibleSelectedStarters(state)
      .filter((s) => s.eventIndex === event.index)
      .sort((a, b) => a.id.localeCompare(b.id)),
  );

  const handleDownload = useCallback(
    async function handleDownload() {
      if (selected.length === 0) {
        receiveStarterTracks(event.eventId, new Map());
      } else {
        const netReplay = event.modules.netReplay;
        const buf: ArrayBuffer = (
          await getPointsBuffer(CDN_HOST, event.eventId, {
            netReplay,
            starterIds: selected.map((s) => s.id),
            range: [new Date(event.startTime), new Date(event.endTime)],
          })
        ).get();
        const decoder = await chunkManager.createDecoder();
        decoder.importRawChunk(buf);
        const decodedChunk = decoder.decodeRawChunk();
        receiveStarterTracks(event.eventId, decodedChunk.exportMap());
      }
    },
    [selected.join(','), event, chunkManager, receiveStarterTracks],
  );

  useEffect(() => {
    handleDownload();
  }, [handleDownload]);

  const handleLoopDownload = useCallback(
    async function handleLoopDownload() {
      if (correctedNow() <= event.endTime) {
        await handleDownload();
      }
    },
    [event, handleDownload],
  );

  useLoop(handleLoopDownload, event.options.pollingInterval);

  return null;
};

export const GroupStarterTrackManager: FC = () => {
  const events = useStore((state) => state.events);
  return (
    <>
      {events.map((event) => (
        <StarterTrackManager key={event.eventId} eventId={event.eventId} />
      ))}
    </>
  );
};
